import { Box, Button, Modal, Paper, Typography, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Checkbox } from "@mui/material";
import { useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { GroupService } from "../../services/group-service";
import { useToast } from "../../context/toast-context";
import { useMemo } from "react";
import * as XLSX from 'xlsx';
import BigNumber from "bignumber.js";
import { getRoundedNumber, getRoundedBigNumber, sumBigNumbers } from "../../utils/roundedNumberHelper";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "75vw",
  height: "75vh",
  maxWidth: "none",
  maxHeight: "none",
  overflowY: "auto",
  bgcolor: "#fff",
  border: "1px solid #898989",
  boxShadow: 24,
  borderRadius: 4,
};

interface ExcelRow {
  index: number;
  name: string;
  percent: number;
  isValid: boolean;
  errors: string[];
}

interface ProcessModalData {
  rows: ExcelRow[];
  totalRows: number;
  validRows: number;
}

interface UploadExcelProps {
  branchId: number;
  branchName: string;
  onUploadSuccess: () => void;
  existingGroups: { name: string; groupId: number; percent: number }[];
}

export function UploadExcel({ branchId, branchName, onUploadSuccess, existingGroups }: UploadExcelProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isProcessModalOpen, setIsProcessModalOpen] = useState(false);
  const [processModalData, setProcessModalData] = useState<ProcessModalData | null>(null);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const { t } = useTranslation();
  const { showToast } = useToast();
  const groupService = useMemo(() => new GroupService(), []);

  const validateExcelData = useCallback((data: any[]): ProcessModalData => {
    const rows: ExcelRow[] = [];
    let validRowsCount = 0;

    const existingGroupNames = existingGroups.map(group => group.name.toLowerCase().trim());
    const existingGroupsMap = new Map(existingGroups.map(group => [group.name.toLowerCase().trim(), group]));

    data.forEach((row, index) => {
      if (index === 0) return; // Saltar encabezados

      // Verificar si la fila está en blanco
      const isEmptyRow = !row[0] && !row[1];
      if (isEmptyRow) return;

      const excelRow: ExcelRow = {
        index: index,
        name: row[0] || "",
        percent: 0,
        isValid: true,
        errors: []
      };

      if (!excelRow.name.trim()) {
        excelRow.errors.push(t("groups.excel.upload.errors.requiredName") || "Nombre del grupo es requerido");
        excelRow.isValid = false;
      } else {
        const groupNameLower = excelRow.name.toLowerCase().trim();
        const groupExists = existingGroupNames.includes(groupNameLower);
        
        if (!groupExists) {
          excelRow.errors.push(t("groups.excel.upload.errors.groupNotFound") || "El grupo no existe en esta sucursal");
          excelRow.isValid = false;
        }
      }

      // Validar porcentaje - verificar si es un número válido
      const percentValue = row[1];
      
      // Verificar si el valor está vacío o es null/undefined
      if (percentValue === null || percentValue === undefined || percentValue === "") {
        excelRow.errors.push(t("groups.excel.upload.errors.requiredPercent") || "Porcentaje es requerido");
        excelRow.isValid = false;
      } else {
        // Convertir el valor a string para procesamiento
        const stringValue = String(percentValue).trim();
        
        const normalizedValue = stringValue.replace(',', '.');
        
        const numericValue = parseFloat(normalizedValue);
        
        if (isNaN(numericValue)) {
          excelRow.errors.push(t("groups.excel.upload.errors.invalidNumber") || "Porcentaje debe ser un número válido");
          excelRow.isValid = false;
        } else {
          // Verificar cuántos decimales tiene el número
          const decimalPart = normalizedValue.split('.')[1];
          const decimalCount = decimalPart ? decimalPart.length : 0;
          
          let finalValue;
          if (decimalCount > 2) {
            // Solo redondear hacia abajo si tiene más de 2 decimales usando roundedNumberHelper
            finalValue = parseFloat(getRoundedNumber(numericValue));
          } else {
            // Si tiene 2 o menos decimales, mantener el valor original
            finalValue = numericValue;
          }
          
          excelRow.percent = finalValue;
          
          if (finalValue < 0) {
            excelRow.errors.push(t("groups.excel.upload.errors.negativePercentages") || "Porcentaje no puede ser negativo");
            excelRow.isValid = false;
          } else if (finalValue > 100) {
            excelRow.errors.push(t("groups.excel.upload.errors.invalidPercentages") || "Porcentaje no puede ser mayor a 100%");
            excelRow.isValid = false;
          }
        }
      }

      if (excelRow.isValid) {
        validRowsCount++;
      }

      rows.push(excelRow);
    });

    // Validar que no haya nombres de grupos duplicados en el Excel
    const groupNameCount = new Map<string, number>();
    rows.forEach(row => {
      const nameLower = row.name.toLowerCase().trim();
      groupNameCount.set(nameLower, (groupNameCount.get(nameLower) || 0) + 1);
    });

    const duplicatedNames = Array.from(groupNameCount.entries())
      .filter(([name, count]) => count > 1)
      .map(([name]) => name);

    if (duplicatedNames.length > 0) {
      rows.forEach(row => {
        const nameLower = row.name.toLowerCase().trim();
        if (duplicatedNames.includes(nameLower)) {
          row.errors.push(t("groups.excel.upload.errors.duplicateName") || `El nombre del grupo "${row.name}" está duplicado. Debe aparecer solo una vez.`);
          row.isValid = false;
          validRowsCount = Math.max(0, validRowsCount - 1);
        }
      });
    }

    const excelGroupNames = rows.map(row => row.name.toLowerCase().trim());
    const missingGroups = existingGroups.filter(group => 
      !excelGroupNames.includes(group.name.toLowerCase().trim())
    );

    if (missingGroups.length > 0) {
      const missingGroupNames = missingGroups.map(group => group.name).join(', ');
      rows.forEach(row => {
        if (row.isValid) {
          row.errors.push(t("groups.excel.upload.errors.missingGroups", { groups: missingGroupNames }) || `Faltan grupos en el Excel: ${missingGroupNames}`);
          row.isValid = false;
        }
      });
      validRowsCount = 0;
    }

    // Validar que la suma total sea 100% - solo con grupos válidos (ya redondeados)
    const validRows = rows.filter(row => row.isValid);
    
    // Usar BigNumber para la suma para evitar problemas de precisión de punto flotante
    const sumBigNumber = sumBigNumbers(validRows.map(row => row.percent));
    
    const totalPercentRounded = getRoundedBigNumber(sumBigNumber);
    const totalPercent = parseFloat(totalPercentRounded);
    
    if (validRows.length > 0 && totalPercent !== 100) {
      rows.forEach(row => {
        if (row.isValid) {
          row.errors.push(`La suma total de grupos válidos debe ser 100%. Actual: ${totalPercent.toFixed(2)}%`);
          row.isValid = false;
        }
      });
      validRowsCount = 0;
    }

    return {
      rows,
      totalRows: rows.length,
      validRows: validRowsCount
    };
  }, [existingGroups, t]);

  const processExcelFile = useCallback(async () => {
    if (!uploadedFile) {
      showToast("Por favor selecciona un archivo", "error");
      return;
    }

    setIsProcessing(true);

    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          const headers = jsonData[0] as string[];
          const requiredColumns = ["Nombre", "Porcentaje"];
          
          const missingColumns = requiredColumns.filter(col => !headers.includes(col));
          if (missingColumns.length > 0) {
            showToast(`Archivo no válido. Faltan columnas: ${missingColumns.join(', ')}`, "error");
            setIsProcessing(false);
            return;
          }

          const validatedData = validateExcelData(jsonData as any[]);
          setProcessModalData(validatedData);
          setSelectedRows(validatedData.rows.filter(row => row.isValid).map(row => row.index));
          setIsProcessModalOpen(true);

        } catch (error) {
          console.error("Error al procesar el archivo Excel:", error);
          showToast("Error al procesar el archivo Excel", "error");
        } finally {
          setIsProcessing(false);
        }
      };

      reader.readAsArrayBuffer(uploadedFile);
    } catch (error) {
      console.error("Error al leer el archivo:", error);
      showToast("Error al leer el archivo", "error");
      setIsProcessing(false);
    }
  }, [uploadedFile, showToast, validateExcelData]);

  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      setProcessModalData(null);
      setSelectedRows([]);
      
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        showToast("El archivo debe ser un Excel (.xlsx o .xls)", "error");
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        showToast("El archivo es demasiado grande (máximo 5MB)", "error");
        return;
      }
    }
  }, [showToast]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        setUploadedFile(file);
        setProcessModalData(null);
        setSelectedRows([]);
      } else {
        showToast("El archivo debe ser un Excel (.xlsx o .xls)", "error");
      }
    }
  }, [showToast]);

  const handleRowSelection = useCallback((rowIndex: number) => {
    setSelectedRows(prev => {
      if (prev.includes(rowIndex)) {
        return prev.filter(index => index !== rowIndex);
      } else {
        return [...prev, rowIndex];
      }
    });
  }, []);

  const handleSelectAllValid = useCallback(() => {
    if (!processModalData) return;
    
    const validRowIndexes = processModalData.rows
      .filter(row => row.isValid)
      .map(row => row.index);
    
    setSelectedRows(validRowIndexes);
  }, [processModalData]);

  const handleDeselectAll = useCallback(() => {
    setSelectedRows([]);
  }, []);

  const clearUploadedFile = useCallback(() => {
    setUploadedFile(null);
    setProcessModalData(null);
    setSelectedRows([]);
    setIsProcessModalOpen(false);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const processSelectedRows = useCallback(async () => {
    if (selectedRows.length === 0) {
      showToast("No hay filas seleccionadas para procesar", "error");
      return;
    }

    setIsProcessing(true);

    try {
      const rowsToProcess = processModalData?.rows.filter(row => 
        selectedRows.includes(row.index) && row.isValid
      );

      if (!rowsToProcess || rowsToProcess.length === 0) {
        showToast("No hay filas válidas seleccionadas", "error");
        setIsProcessing(false);
        return;
      }

      const formData = new FormData();
      formData.append('file', uploadedFile!);

      await groupService.uploadGroupsExcel(
        branchId,
        uploadedFile!,
        (data) => {
          showToast(t("groups.excel.upload.success"), "success");
          setIsProcessModalOpen(false);
          setIsModalOpen(false);
          onUploadSuccess();
          clearUploadedFile();
        },
        (errorMessage) => {
          console.error("Error del backend:", errorMessage);
          showToast(errorMessage, "error");
        }
      );
    } finally {
      setIsProcessing(false);
    }
  }, [selectedRows, processModalData, uploadedFile, branchId, groupService, showToast, t, onUploadSuccess, clearUploadedFile]);

  const handleClose = () => {
    setIsModalOpen(false);
    clearUploadedFile();
  };

  return (
    <>
      <Button
        variant="contained"
        disableElevation
        onClick={() => setIsModalOpen(true)}
        sx={{
          background: "#2d2f32",
          margin: 0,
          padding: "0 10px",
          borderRadius: "999px",
          textTransform: "none",
          fontFamily: "Montserrat",
          fontWeight: 300,
          "&:hover": {
            background: "#1a1c1f",
          },
        }}
        endIcon={
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            stroke="#EF0BB8"
            strokeWidth="0"
          >
            <path
              d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2M18 20H6V4H13V9H18V20Z"
              fill="#EF0BB8"
              stroke="none"
            />
            <path
              d="M12 12L12 16"
              stroke="#EF0BB8"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path
              d="M10 14L14 14"
              stroke="#EF0BB8"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        }
      >
        {t("groups.excel.upload.button")}
      </Button>
      
      {isModalOpen && (
        <Modal
          open={isModalOpen}
          onClose={handleClose}
          aria-labelledby="Cargar Excel"
          aria-describedby="Cargar archivo Excel con porcentajes de grupos"
        >
          <Box sx={style}>
            <div className="bg-gray-light text-center text-xl font-normal text-black py-8 px-2">
              <h1>{t("groups.excel.upload.title")}</h1>
            </div>
            <div className="p-8 h-full">
              <div className="grid grid-cols-2 gap-8 h-full">
                <div className="text-center py-8 border-r border-gray-200">
                  <div className="mb-6">
                    <h3 className="text-lg font-medium text-gray-800 mb-4">
                      Instrucciones
                    </h3>
                    <p className="text-gray-600 mb-6">
                      {t("groups.excel.upload.instructions")}
                    </p>
                  </div>
                  
                  <div className="text-sm text-gray-500">
                    <p className="font-medium text-gray-700 mb-2">Estructura del Excel:</p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>Columna "Nombre" - Nombre del grupo</li>
                      <li>Columna "Porcentaje" - Porcentaje del grupo</li>
                    </ul>
                    <p className="mt-3 font-medium text-gray-700">Requisitos:</p>
                    <ul className="list-disc list-inside mt-1 space-y-1">
                      <li>La suma de porcentajes debe ser exactamente 100%</li>
                      <li>Solo modifica la columna de porcentajes</li>
                      <li>No agregues ni elimines grupos</li>
                      <li>Puedes usar coma (,) o punto (.) como separador decimal</li>
                    </ul>
                  </div>
                </div>

                {/* Segunda mitad - Carga de archivo */}
                <div className="py-8">
                  <div className="text-center mb-6">
                    <h3 className="text-lg font-medium text-gray-800 mb-4">
                      Cargar Archivo Excel
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Sube tu archivo Excel completado para procesar los grupos
                    </p>
                  </div>

                  <Paper
                    elevation={2}
                    sx={{
                      border: uploadedFile ? "2px solid #EF0BB8" : isDragOver ? "2px solid #EF0BB8" : "2px dashed #ccc",
                      borderRadius: 2,
                      padding: 4,
                      textAlign: "center",
                      marginBottom: 3,
                      backgroundColor: uploadedFile ? "#f8f4ff" : isDragOver ? "#f8f4ff" : "#fafafa",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        borderColor: "#EF0BB8",
                        backgroundColor: "#f8f4ff",
                      },
                    }}
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileUpload}
                      style={{ display: 'none' }}
                    />
                    
                    {uploadedFile ? (
                      <div>
                        <svg
                          width="48"
                          height="48"
                          viewBox="0 0 24 24"
                          fill="#EF0BB8"
                          className="mx-auto mb-2"
                        >
                          <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2M18 20H6V4H13V9H18V20Z"/>
                        </svg>
                        <Typography variant="h6" sx={{ color: "#EF0BB8", fontWeight: "bold" }}>
                          {uploadedFile.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#666", mt: 1 }}>
                          Archivo cargado exitosamente
                        </Typography>
                      </div>
                    ) : (
                      <div>
                        <svg
                          width="48"
                          height="48"
                          viewBox="0 0 24 24"
                          fill={isDragOver ? "#EF0BB8" : "#ccc"}
                          className="mx-auto mb-2"
                        >
                          <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2M18 20H6V4H13V9H18V20Z"/>
                        </svg>
                        <Typography variant="h6" sx={{ color: isDragOver ? "#EF0BB8" : "#666" }}>
                          {isDragOver ? "¡Suelta el archivo aquí!" : "Arrastra y suelta tu archivo Excel aquí"}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#999", mt: 1 }}>
                          o haz clic para seleccionar un archivo
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#999", display: "block", mt: 2 }}>
                          Formatos soportados: .xlsx, .xls
                        </Typography>
                      </div>
                    )}
                  </Paper>

                  {uploadedFile && (
                    <div className="text-center space-y-3">
                      <div className="flex gap-3 justify-center">
                        <Button
                          variant="contained"
                          onClick={processExcelFile}
                          disabled={isProcessing}
                          sx={{
                            backgroundColor: "#EF0BB8",
                            color: "white",
                            fontFamily: "Montserrat",
                            fontWeight: 500,
                            textTransform: "none",
                            paddingX: 4,
                            paddingY: 1.5,
                            borderRadius: 2,
                            "&:hover": {
                              backgroundColor: "#d009a0",
                            },
                            "&:disabled": {
                              backgroundColor: "#ccc",
                            },
                          }}
                        >
                          {isProcessing ? "Procesando..." : "Procesar Archivo"}
                        </Button>
                        
                        <Button
                          variant="outlined"
                          onClick={clearUploadedFile}
                          disabled={isProcessing}
                          sx={{
                            borderColor: "#666",
                            color: "#666",
                            fontFamily: "Montserrat",
                            fontWeight: 500,
                            textTransform: "none",
                            paddingX: 4,
                            paddingY: 1.5,
                            borderRadius: 2,
                            "&:hover": {
                              borderColor: "#333",
                              backgroundColor: "#f5f5f5",
                              color: "#333",
                            },
                            "&:disabled": {
                              borderColor: "#ccc",
                              color: "#ccc",
                            },
                          }}
                        >
                          Limpiar
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Box>
        </Modal>
      )}

      <Dialog
        open={isProcessModalOpen}
        onClose={() => setIsProcessModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "80vh",
            height: "80vh",
          }
        }}
      >
        <DialogTitle sx={{ backgroundColor: "#f5f5f5", borderBottom: "1px solid #e0e0e0" }}>
          <Typography variant="h6" fontWeight="bold">
            Revisar Datos del Excel
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Selecciona las filas que deseas procesar. Las filas con errores aparecen en rojo.
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ padding: 0 }}>
          {processModalData && (
            <>
              <Box sx={{ padding: 2, backgroundColor: "#f8f9fa", borderBottom: "1px solid #e0e0e0" }}>
                <div className="flex justify-between items-center mb-2">
                  <Typography variant="subtitle1" fontWeight="bold">
                    Resumen: {processModalData.validRows} de {processModalData.totalRows} filas válidas
                  </Typography>
                  <div className="flex gap-2">
                    <Button
                      size="small"
                      onClick={handleSelectAllValid}
                      sx={{ fontSize: "0.75rem", textTransform: "none" }}
                    >
                      Seleccionar todas válidas
                    </Button>
                    <Button
                      size="small"
                      onClick={handleDeselectAll}
                      sx={{ fontSize: "0.75rem", textTransform: "none" }}
                    >
                      Deseleccionar todas
                    </Button>
                  </div>
                </div>
              </Box>

              <Box sx={{ padding: 2, backgroundColor: "#f8f9fa", borderBottom: "1px solid #e0e0e0" }}>
                <div className="grid grid-cols-2 gap-4">
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ color: "#666" }}>
                    Nombre
                  </Typography>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ color: "#666" }}>
                    Porcentaje
                  </Typography>
                </div>
              </Box>

              <List sx={{ maxHeight: "400px", overflow: "auto" }}>
                {processModalData.rows.map((row, index) => (
                  <ListItem key={index} divider sx={{ backgroundColor: row.isValid ? "#fff" : "#fff5f5" }}>
                    <ListItemButton 
                      onClick={() => handleRowSelection(row.index)}
                      disabled={!row.isValid}
                      sx={{
                        padding: 1,
                        "&:hover": {
                          backgroundColor: row.isValid ? "#f8f4ff" : "#fff0f0",
                        },
                      }}
                    >
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={selectedRows.includes(row.index)}
                          tabIndex={-1}
                          disableRipple
                          disabled={!row.isValid}
                          sx={{
                            color: "#EF0BB8",
                            "&.Mui-checked": {
                              color: "#EF0BB8",
                            },
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Typography variant="body2" fontWeight="bold">
                                {row.name || "Sin nombre"}
                              </Typography>
                            </div>
                            <div>
                              <Typography variant="body2">
                                {row.percent}%
                              </Typography>
                            </div>
                          </div>
                        }
                        secondary={
                          !row.isValid && row.errors.length > 0 ? (
                            <div className="mt-1">
                              {row.errors.map((error, errorIndex) => (
                                <Typography 
                                  key={errorIndex} 
                                  variant="caption" 
                                  sx={{ color: "#d32f2f", display: "block" }}
                                >
                                  • {error}
                                </Typography>
                              ))}
                            </div>
                          ) : null
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>

              {processModalData.rows.length === 0 && (
                <Box sx={{ padding: 4, textAlign: "center" }}>
                  <Typography variant="body1" color="textSecondary">
                    No se encontraron filas para procesar
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        
        <DialogActions sx={{ padding: 2, borderTop: "1px solid #e0e0e0", backgroundColor: "#f5f5f5" }}>
          <Button 
            onClick={() => setIsProcessModalOpen(false)}
            sx={{ 
              color: "#666",
              fontFamily: "Montserrat",
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={selectedRows.length === 0 || isProcessing}
            onClick={processSelectedRows}
            sx={{
              backgroundColor: "#EF0BB8",
              color: "white",
              fontFamily: "Montserrat",
              fontWeight: 500,
              "&:hover": {
                backgroundColor: "#d009a0",
              },
              "&:disabled": {
                backgroundColor: "#ccc",
              },
            }}
          >
            {isProcessing ? "Procesando..." : `Procesar Seleccionadas (${selectedRows.length})`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}